import React, { FC, useRef, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-use';
import { useEventCallback } from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import axios from 'axios';
import Masonry from 'react-responsive-masonry';
import { Buffer } from 'buffer';
import { Template } from './data/template';
import { useTranslation } from 'react-i18next';
import { createBreakpoint } from 'react-use';
import { defaultBreakpoints } from '../../constant/breakpoints';
import TemplateModal from '../../TemplateModal';

const TemplatePanel: FC<{
  type: string;
  keyword: string;
  addPage: (template: Template) => void;
}> = ({ type, keyword, addPage }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef(false);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { state, query } = useEditor();
  const useBreakpoint = createBreakpoint(defaultBreakpoints);
  const breakpoint = useBreakpoint();

  const columnCounts = useMemo(() => {
    if (breakpoint === 'md') {
      return 3;
    }

    return 2;
  }, [breakpoint]);

  const { t } = useTranslation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const loadTemplateList = useEventCallback(async (isInitialLoad = false) => {
    dataRef.current = true;
    setIsLoading(true);
    const params = {
      page: isInitialLoad ? '1' : page.toString(),
      limit: '20',
      type: queryParams.get('type') || 'frame',
      q: keyword || 'template',
    };

    const response = await axios.get<string[]>(
      `/templates?${new URLSearchParams(params).toString()}`
    );

    const { data } = response;

    const nextTemplates = data.map((raw) => {
      const dt = JSON.parse(raw);
      const buffer = Buffer.from(dt.elements, 'base64');
      const decodedString = buffer.toString('utf-8');

      dt.elements = JSON.parse(decodedString);

      return dt;
    });

    if (isInitialLoad) {
      setTemplates(nextTemplates);
      setPage(2); // Reset to the next page to load
      setNoResults(nextTemplates.length === 0);
    } else {
      setTemplates((prevState) => [...prevState, ...nextTemplates]);
      setPage((prevPage) => prevPage + 1);
    }

    setIsLoading(false);

    if (response.data.length > 0) {
      dataRef.current = false;
    }
  });

  const handleChooseTemplate = (template: Template, force: Boolean = false) => {
    setSelectedTemplate(template);

    const isContainLayers =
      Object.entries(query.getLayers(state.activePage)).filter(([type, _l]) => {
        return type !== 'ROOT';
      }).length > 0;

    if (isContainLayers && !force) {
      setShowConfirmModal(true);
      return;
    }

    setShowConfirmModal(false);
    addPage(template);
    setSelectedTemplate(null);
  };

  useEffect(() => {
    loadTemplateList(true);
  }, [keyword]);

  useEffect(() => {
    const handleLoadMore = async (e: Event) => {
      const node = e.target as HTMLDivElement;
      if (
        node.scrollHeight - node.scrollTop - 80 <= node.clientHeight &&
        !dataRef.current
      ) {
        await loadTemplateList();
      }
    };
    scrollRef.current?.addEventListener('scroll', handleLoadMore);
    return () => {
      scrollRef.current?.removeEventListener('scroll', handleLoadMore);
    };
  }, [loadTemplateList]);

  return (
    <div
      ref={scrollRef}
      css={(theme) => ({
        flexGrow: 1,
        position: 'relative',
        height: 0,
        overflow: 'auto',
        padding: '16px',
        paddingTop: 8,
        gridGap: 8,
      })}
    >
      <TemplateModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleChooseTemplate(selectedTemplate, true)}
      />

      {noResults && !isLoading ? (
        <div
          css={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            fontSize: 16,
          })}
        >
          {t('page_editor.error.no_results', { query: keyword })}
        </div>
      ) : (
        <>
          <Masonry columnsCount={columnCounts} gutter="6px">
            {templates.map((item, index) => (
              <div
                key={index}
                css={(theme) => ({
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '4px',
                  borderRadius: 8,
                  transition: 'box-shadow 0.3s',
                  ':hover': {
                    boxShadow: theme.shadow.hoverItem,
                  },
                  aspectRatio: '1/1',
                })}
                onClick={() => handleChooseTemplate(item)}
              >
                <img
                  css={(theme) => ({
                    height: '100%',
                    width: 'auto',
                  })}
                  loading="lazy"
                  src={item.img}
                />
              </div>
            ))}
          </Masonry>

          {isLoading && (
            <div
              css={(theme) => ({
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontSize: 16,
              })}
            >
              Loading...
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplatePanel;
